import { useState, useEffect } from 'react';
import { SyncToggle } from '@/components/SyncToggle';
import { useSyncConfiguration } from '@/hooks/useSyncConfiguration';
import { useXeroAuth } from '@/hooks/useXeroAuth';
import { LoadingSpinner } from '@/components/LoadingSpinner';
import { SyncConfiguration, ProductVariantHandling, GHLProductType } from '@/types';
import { AlertCircle, Save, Info, AlertTriangle, Trash2, CheckCircle } from 'lucide-react';
import { useUninstall } from '@/hooks/useUninstall';
import { cn } from '@/utils/cn';

export function SettingsPage() {
  const { isConnected } = useXeroAuth();
  const { config, isLoading, error, updateConfig, isUpdating } =
    useSyncConfiguration();
  const { uninstall, isUninstalling, isUninstalled } = useUninstall();
  const [uninstallConfirmText, setUninstallConfirmText] = useState('');

  const [formData, setFormData] = useState<Partial<SyncConfiguration>>({
    syncContacts: false,
    syncProducts: false,
    syncInvoices: false,
    syncContactsWithInvoicesOnly: false,
    productVariantHandling: 'ignore',
    syncProductInventory: false,
    inventorySourceOfTruth: 'xero',
    defaultProductType: 'DIGITAL',
    defaultCurrency: 'AUD',
    syncIntervalMinutes: 60,
    conflictResolution: 'highlevel_wins',
    notifyOnSyncSuccess: false,
    notifyOnSyncFailure: true,
    autoSyncEnabled: false,
    invoiceCreationSystem: 'highlevel',
    paymentHandlingSystem: 'highlevel',
    emailSendingSystem: 'highlevel',
    // Cascade delete defaults (all disabled by default for safety)
    cascadeDeleteContactToXero: false,
    cascadeDeleteContactToHighLevel: false,
    cascadeDeleteProductToXero: false,
    cascadeDeleteProductToHighLevel: false,
    cascadeDeleteGracePeriodEnabled: true,
    cascadeDeleteGracePeriodHours: 24,
    notifyOnCascadeDelete: true,
  });

  useEffect(() => {
    if (config) {
      setFormData({
        syncContacts: config.syncContacts,
        syncProducts: config.syncProducts,
        syncInvoices: config.syncInvoices,
        syncContactsWithInvoicesOnly: config.syncContactsWithInvoicesOnly,
        productVariantHandling: config.productVariantHandling,
        syncProductInventory: config.syncProductInventory,
        inventorySourceOfTruth: config.inventorySourceOfTruth,
        defaultProductType: config.defaultProductType,
        defaultCurrency: config.defaultCurrency,
        syncIntervalMinutes: config.syncIntervalMinutes,
        conflictResolution: config.conflictResolution,
        notifyOnSyncSuccess: config.notifyOnSyncSuccess,
        notifyOnSyncFailure: config.notifyOnSyncFailure,
        autoSyncEnabled: config.autoSyncEnabled,
        invoiceCreationSystem: config.invoiceCreationSystem,
        paymentHandlingSystem: config.paymentHandlingSystem,
        emailSendingSystem: config.emailSendingSystem,
        // Cascade delete settings
        cascadeDeleteContactToXero: config.cascadeDeleteContactToXero ?? false,
        cascadeDeleteContactToHighLevel: config.cascadeDeleteContactToHighLevel ?? false,
        cascadeDeleteProductToXero: config.cascadeDeleteProductToXero ?? false,
        cascadeDeleteProductToHighLevel: config.cascadeDeleteProductToHighLevel ?? false,
        cascadeDeleteGracePeriodEnabled: config.cascadeDeleteGracePeriodEnabled ?? true,
        cascadeDeleteGracePeriodHours: config.cascadeDeleteGracePeriodHours ?? 24,
        notifyOnCascadeDelete: config.notifyOnCascadeDelete ?? true,
      });
    }
  }, [config]);

  const handleSave = () => {
    updateConfig(formData);
  };

  const hasChanges = JSON.stringify(formData) !== JSON.stringify(config);

  if (isUninstalled) {
    return (
      <div className="max-w-4xl mx-auto">
        <div className="rounded-lg border border-green-200 bg-green-50 p-8 text-center">
          <CheckCircle className="h-12 w-12 text-green-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-green-900 mb-2">
            Plugin Successfully Uninstalled
          </h2>
          <p className="text-sm text-green-700 mb-4">
            Both HighLevel and Xero connections have been disconnected. Your sync data has been
            archived and will be permanently deleted after 30 days.
          </p>
          <p className="text-xs text-green-600">
            You can close this window or reinstall the plugin at any time.
          </p>
        </div>
      </div>
    );
  }

  if (!isConnected) {
    return (
      <div className="max-w-4xl mx-auto">
        <div className="rounded-lg border border-yellow-200 bg-yellow-50 p-4">
          <div className="flex items-start">
            <AlertCircle className="h-5 w-5 text-yellow-400" />
            <div className="ml-3">
              <h3 className="text-sm font-medium text-yellow-800">
                Connection Required
              </h3>
              <p className="mt-1 text-sm text-yellow-700">
                Please connect to Xero before configuring sync settings.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Sync Settings</h1>
          <p className="mt-1 text-sm text-gray-500">
            Configure how data syncs between HighLevel and Xero
          </p>
        </div>
        <button
          onClick={handleSave}
          disabled={!hasChanges || isUpdating}
          className={cn(
            'inline-flex items-center space-x-2 rounded-md px-4 py-2 text-sm font-medium transition-colors',
            'bg-primary-600 text-white hover:bg-primary-700',
            'disabled:opacity-50 disabled:cursor-not-allowed'
          )}
        >
          <Save className="h-4 w-4" />
          <span>{isUpdating ? 'Saving...' : 'Save Changes'}</span>
        </button>
      </div>

      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4">
          <div className="flex items-start">
            <AlertCircle className="h-5 w-5 text-red-400" />
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Error</h3>
              <p className="mt-1 text-sm text-red-700">
                {error.message || 'Failed to load configuration'}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Master Toggle */}
      <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
        <h2 className="text-lg font-medium text-gray-900 mb-4">
          Sync Status
        </h2>
        <SyncToggle
          label="Enable Automatic Sync"
          description="When enabled, data will sync automatically at the specified frequency"
          enabled={formData.autoSyncEnabled || false}
          onChange={(enabled) => setFormData({ ...formData, autoSyncEnabled: enabled })}
        />
      </div>

      {/* Entity Selection */}
      <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
        <h2 className="text-lg font-medium text-gray-900 mb-4">
          What to Sync
        </h2>
        <div className="divide-y divide-gray-200">
          <SyncToggle
            label="Contacts"
            description="Sync customer and contact information"
            enabled={formData.syncContacts || false}
            onChange={(enabled) =>
              setFormData({ ...formData, syncContacts: enabled })
            }
          />
          <SyncToggle
            label="Products"
            description="Sync product catalog and pricing"
            enabled={formData.syncProducts || false}
            onChange={(enabled) =>
              setFormData({ ...formData, syncProducts: enabled })
            }
          />
          <SyncToggle
            label="Invoices"
            description="Sync invoices and payment status"
            enabled={formData.syncInvoices || false}
            onChange={(enabled) =>
              setFormData({ ...formData, syncInvoices: enabled })
            }
          />
        </div>
      </div>

      {/* Smart Contact Filtering */}
      {formData.syncContacts && (
        <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
          <h2 className="text-lg font-medium text-gray-900 mb-4">
            Contact Sync Options
          </h2>
          <SyncToggle
            label="Only Sync Contacts with Invoices"
            description="Reduce noise by only syncing contacts that have associated invoices or orders"
            enabled={formData.syncContactsWithInvoicesOnly || false}
            onChange={(enabled) =>
              setFormData({ ...formData, syncContactsWithInvoicesOnly: enabled })
            }
          />
        </div>
      )}

      {/* Product Sync Options */}
      {formData.syncProducts && (
        <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
          <h2 className="text-lg font-medium text-gray-900 mb-4">
            Product Sync Options
          </h2>
          <div className="space-y-6">
            {/* Variant Handling */}
            <div>
              <label
                htmlFor="variantHandling"
                className="block text-sm font-medium text-gray-700"
              >
                How should product variants be handled?
              </label>
              <select
                id="variantHandling"
                value={formData.productVariantHandling}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    productVariantHandling: e.target.value as ProductVariantHandling,
                  })
                }
                className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
              >
                <option value="ignore">Ignore variants (sync base product only)</option>
                <option value="separate_items">Create separate Xero item for each variant</option>
              </select>
              <p className="mt-2 text-sm text-gray-500">
                {formData.productVariantHandling === 'ignore'
                  ? 'Only the base product will sync, variant information is ignored'
                  : 'Each variant (e.g., "Red, Large") creates a separate Xero item with SKU like "SHIRT-RED-L"'}
              </p>
            </div>

            {/* Inventory Sync Toggle */}
            <div className="border-t border-gray-200 pt-4">
              <SyncToggle
                label="Sync Inventory Quantities"
                description="Enable synchronization of product stock quantities between systems"
                enabled={formData.syncProductInventory || false}
                onChange={(enabled) =>
                  setFormData({ ...formData, syncProductInventory: enabled })
                }
              />

              {/* Inventory Source of Truth - Only shown when inventory sync is enabled */}
              {formData.syncProductInventory && (
                <div className="mt-4 pl-12 space-y-3">
                  <div>
                    <label
                      htmlFor="inventorySourceOfTruth"
                      className="block text-sm font-medium text-gray-700"
                    >
                      Inventory Source of Truth
                    </label>
                    <select
                      id="inventorySourceOfTruth"
                      value={formData.inventorySourceOfTruth || 'xero'}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          inventorySourceOfTruth: e.target.value as 'xero' | 'highlevel',
                        })
                      }
                      className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                    >
                      <option value="xero">Xero (overwrite HighLevel quantities)</option>
                      <option value="highlevel">HighLevel (overwrite Xero quantities)</option>
                    </select>
                    <p className="mt-2 text-sm text-gray-500">
                      {formData.inventorySourceOfTruth === 'xero'
                        ? 'Xero quantities will automatically overwrite HighLevel during sync. Xero quantities are calculated from transactions.'
                        : 'HighLevel quantities will automatically overwrite Xero during sync. Manual changes in Xero will be overwritten.'}
                    </p>
                  </div>
                  <div className="rounded-md border border-blue-200 bg-blue-50 p-3">
                    <p className="text-sm text-blue-700">
                      <strong>Note:</strong> Manual sync from one system to another will override this preference and update the destination with the source system's data.
                      For bidirectional sync that respects your source of truth preference, use the "Bidirectional" sync option.
                    </p>
                  </div>
                </div>
              )}
            </div>

            {/* Default Product Type */}
            <div className="border-t border-gray-200 pt-4">
              <label
                htmlFor="defaultProductType"
                className="block text-sm font-medium text-gray-700"
              >
                Default Product Type
              </label>
              <select
                id="defaultProductType"
                value={formData.defaultProductType || 'DIGITAL'}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    defaultProductType: e.target.value as GHLProductType,
                  })
                }
                className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
              >
                <option value="DIGITAL">Digital (services, subscriptions, downloads)</option>
                <option value="PHYSICAL">Physical (tangible goods that ship)</option>
              </select>
              <p className="mt-2 text-sm text-gray-500">
                {formData.defaultProductType === 'PHYSICAL'
                  ? 'Products will be created as physical goods in HighLevel (for items that need shipping)'
                  : 'Products will be created as digital items in HighLevel (for services, software, subscriptions)'}
              </p>
            </div>

            {/* Default Currency */}
            <div className="border-t border-gray-200 pt-4">
              <label
                htmlFor="defaultCurrency"
                className="block text-sm font-medium text-gray-700"
              >
                Default Currency
              </label>
              <select
                id="defaultCurrency"
                value={formData.defaultCurrency || 'AUD'}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    defaultCurrency: e.target.value,
                  })
                }
                className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
              >
                <option value="AUD">AUD - Australian Dollar</option>
                <option value="USD">USD - US Dollar</option>
                <option value="GBP">GBP - British Pound</option>
                <option value="EUR">EUR - Euro</option>
                <option value="NZD">NZD - New Zealand Dollar</option>
                <option value="CAD">CAD - Canadian Dollar</option>
              </select>
              <p className="mt-2 text-sm text-gray-500">
                Currency used for product pricing when syncing between systems
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Sync Frequency */}
      <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
        <h2 className="text-lg font-medium text-gray-900 mb-4">
          Sync Frequency
        </h2>
        <div>
          <label
            htmlFor="frequency"
            className="block text-sm font-medium text-gray-700"
          >
            How often should data sync?
          </label>
          <select
            id="frequency"
            value={formData.syncIntervalMinutes}
            onChange={(e) =>
              setFormData({
                ...formData,
                syncIntervalMinutes: parseInt(e.target.value),
              })
            }
            className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
          >
            <option value={5}>Every 5 minutes</option>
            <option value={15}>Every 15 minutes</option>
            <option value={30}>Every 30 minutes</option>
            <option value={60}>Every hour</option>
            <option value={180}>Every 3 hours</option>
            <option value={360}>Every 6 hours</option>
            <option value={720}>Every 12 hours</option>
            <option value={1440}>Daily</option>
          </select>
          <p className="mt-2 text-sm text-gray-500">
            Lower frequencies may impact API rate limits
          </p>
        </div>
      </div>

      {/* Source of Truth Summary Table */}
      <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
        <h2 className="text-lg font-medium text-gray-900 mb-4">
          Source of Truth Summary
        </h2>
        <p className="text-sm text-gray-500 mb-4">
          This table shows which system is configured as authoritative for different data types
        </p>
        <div className="overflow-hidden border border-gray-200 rounded-md">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Data Type
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Source of Truth
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Description
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              <tr>
                <td className="px-4 py-3 text-sm font-medium text-gray-900">
                  General Data Conflicts
                </td>
                <td className="px-4 py-3 text-sm text-gray-900">
                  {formData.conflictResolution === 'newest_wins'
                    ? 'Newest Wins'
                    : formData.conflictResolution === 'xero_wins'
                    ? 'Xero'
                    : 'HighLevel'}
                </td>
                <td className="px-4 py-3 text-sm text-gray-500">
                  {formData.conflictResolution === 'newest_wins'
                    ? 'Most recently updated data will be used for product names, descriptions, prices, etc.'
                    : `${formData.conflictResolution === 'xero_wins' ? 'Xero' : 'HighLevel'} data will overwrite conflicts for product metadata`}
                </td>
              </tr>
              {formData.syncProductInventory && (
                <tr>
                  <td className="px-4 py-3 text-sm font-medium text-gray-900">
                    Inventory Quantities
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-900">
                    {formData.inventorySourceOfTruth === 'xero' ? 'Xero' : 'HighLevel'}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-500">
                    {formData.inventorySourceOfTruth === 'xero'
                      ? 'Xero stock quantities will overwrite HighLevel quantities during sync'
                      : 'HighLevel stock quantities will overwrite Xero quantities during sync'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Conflict Resolution */}
      <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
        <h2 className="text-lg font-medium text-gray-900 mb-4">
          Conflict Resolution
        </h2>
        <div>
          <label
            htmlFor="conflict"
            className="block text-sm font-medium text-gray-700"
          >
            When data conflicts, which version should win?
          </label>
          <select
            id="conflict"
            value={formData.conflictResolution}
            onChange={(e) =>
              setFormData({
                ...formData,
                conflictResolution: e.target.value as
                  | 'highlevel_wins'
                  | 'xero_wins'
                  | 'newest_wins',
              })
            }
            className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
          >
            <option value="highlevel_wins">HighLevel Wins</option>
            <option value="xero_wins">Xero Wins</option>
            <option value="newest_wins">Newest Wins</option>
          </select>
          <p className="mt-2 text-sm text-gray-500">
            {formData.conflictResolution === 'newest_wins'
              ? 'The most recently updated version will be used'
              : `${
                  formData.conflictResolution === 'highlevel_wins'
                    ? 'HighLevel'
                    : 'Xero'
                } data will automatically overwrite conflicts`}
          </p>
        </div>
      </div>

      {/* Workflow Configuration */}
      <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
        <h2 className="text-lg font-medium text-gray-900 mb-4">
          Workflow Configuration
        </h2>
        <p className="text-sm text-gray-500 mb-6">
          Define which system is authoritative for specific business operations
        </p>
        <div className="space-y-6">
          {/* Invoice Creation */}
          <div>
            <label
              htmlFor="invoiceCreation"
              className="block text-sm font-medium text-gray-700"
            >
              Where are invoices created?
            </label>
            <select
              id="invoiceCreation"
              value={formData.invoiceCreationSystem}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  invoiceCreationSystem: e.target.value as 'highlevel' | 'xero' | 'both',
                })
              }
              className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
            >
              <option value="highlevel">HighLevel (sync to Xero)</option>
              <option value="xero">Xero (sync from Xero)</option>
              <option value="both">Both systems</option>
            </select>
            <p className="mt-2 text-sm text-gray-500">
              {formData.invoiceCreationSystem === 'highlevel'
                ? 'Invoices created in HighLevel will sync to Xero'
                : formData.invoiceCreationSystem === 'xero'
                ? 'Invoices created in Xero will sync to HighLevel'
                : 'Invoices can be created in either system and sync bidirectionally'}
            </p>
          </div>

          {/* Payment Handling */}
          <div>
            <label
              htmlFor="paymentHandling"
              className="block text-sm font-medium text-gray-700"
            >
              Where are payments processed?
            </label>
            <select
              id="paymentHandling"
              value={formData.paymentHandlingSystem}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  paymentHandlingSystem: e.target.value as 'highlevel' | 'xero' | 'both',
                })
              }
              className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
            >
              <option value="highlevel">HighLevel (sync to Xero)</option>
              <option value="xero">Xero (sync from Xero)</option>
              <option value="both">Both systems</option>
            </select>
            <p className="mt-2 text-sm text-gray-500">
              {formData.paymentHandlingSystem === 'highlevel'
                ? 'Payments processed in HighLevel will sync to Xero'
                : formData.paymentHandlingSystem === 'xero'
                ? 'Payments processed in Xero will sync to HighLevel'
                : 'Payments can be processed in either system and sync bidirectionally'}
            </p>
          </div>

          {/* Email Sending */}
          <div>
            <label
              htmlFor="emailSending"
              className="block text-sm font-medium text-gray-700"
            >
              Which system sends invoice emails?
            </label>
            <select
              id="emailSending"
              value={formData.emailSendingSystem}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  emailSendingSystem: e.target.value as 'highlevel' | 'xero' | 'both',
                })
              }
              className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
            >
              <option value="highlevel">HighLevel</option>
              <option value="xero">Xero</option>
              <option value="both">Both systems</option>
            </select>
            <p className="mt-2 text-sm text-gray-500">
              {formData.emailSendingSystem === 'highlevel'
                ? 'HighLevel will send invoice and payment emails'
                : formData.emailSendingSystem === 'xero'
                ? 'Xero will send invoice and payment emails'
                : 'Both systems will send emails (may result in duplicate emails)'}
            </p>
          </div>
        </div>
      </div>

      {/* Notifications */}
      <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
        <h2 className="text-lg font-medium text-gray-900 mb-4">
          Notifications
        </h2>
        <div className="divide-y divide-gray-200">
          <SyncToggle
            label="Notify on Successful Sync"
            description="Receive notifications when syncs complete successfully"
            enabled={formData.notifyOnSyncSuccess || false}
            onChange={(enabled) =>
              setFormData({ ...formData, notifyOnSyncSuccess: enabled })
            }
          />
          <SyncToggle
            label="Notify on Errors"
            description="Receive notifications when syncs fail or encounter errors"
            enabled={formData.notifyOnSyncFailure || false}
            onChange={(enabled) =>
              setFormData({ ...formData, notifyOnSyncFailure: enabled })
            }
          />
        </div>
      </div>

      {/* Cascade Delete Settings - Danger Zone */}
      <div className="rounded-lg border-2 border-red-300 bg-red-50 p-6 shadow-sm">
        <div className="flex items-center space-x-2 mb-4">
          <AlertTriangle className="h-5 w-5 text-red-600" />
          <h2 className="text-lg font-medium text-red-900">
            Cascade Delete Settings
          </h2>
          <span className="inline-flex items-center rounded-full bg-red-100 px-2.5 py-0.5 text-xs font-medium text-red-800">
            Danger Zone
          </span>
        </div>

        {/* Warning Box */}
        <div className="rounded-md border border-red-200 bg-red-100 p-4 mb-6">
          <div className="flex">
            <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0" />
            <div className="ml-3">
              <p className="text-sm text-red-800">
                <strong>Warning:</strong> These settings control automatic deletion of data across systems.
                When enabled, deleting a contact or product in one system can automatically delete the
                corresponding record in the other system. This action is irreversible.
              </p>
            </div>
          </div>
        </div>

        {/* Contact Deletion */}
        <div className="space-y-6">
          <div>
            <h3 className="text-sm font-medium text-gray-900 mb-3">Contact Deletion</h3>
            <div className="space-y-3 pl-4 border-l-2 border-red-200">
              <SyncToggle
                label="Delete in Xero when deleted in HighLevel"
                description="When a contact is deleted in HighLevel, automatically archive the linked contact in Xero"
                enabled={formData.cascadeDeleteContactToXero || false}
                onChange={(enabled) =>
                  setFormData({ ...formData, cascadeDeleteContactToXero: enabled })
                }
              />
              <SyncToggle
                label="Delete in HighLevel when deleted in Xero"
                description="When a contact is deleted/archived in Xero, automatically delete the linked contact in HighLevel"
                enabled={formData.cascadeDeleteContactToHighLevel || false}
                onChange={(enabled) =>
                  setFormData({ ...formData, cascadeDeleteContactToHighLevel: enabled })
                }
              />
            </div>
            <p className="mt-2 text-xs text-gray-500 pl-4">
              Note: Contacts with linked invoices cannot be deleted (protected by business rules).
            </p>
          </div>

          {/* Product Deletion */}
          <div>
            <h3 className="text-sm font-medium text-gray-900 mb-3">Product Deletion</h3>
            <div className="space-y-3 pl-4 border-l-2 border-red-200">
              <SyncToggle
                label="Delete in Xero when deleted in HighLevel"
                description="When a product is deleted in HighLevel, automatically delete the linked item in Xero"
                enabled={formData.cascadeDeleteProductToXero || false}
                onChange={(enabled) =>
                  setFormData({ ...formData, cascadeDeleteProductToXero: enabled })
                }
              />
              <SyncToggle
                label="Delete in HighLevel when deleted in Xero"
                description="When an item is deleted in Xero, automatically delete the linked product in HighLevel"
                enabled={formData.cascadeDeleteProductToHighLevel || false}
                onChange={(enabled) =>
                  setFormData({ ...formData, cascadeDeleteProductToHighLevel: enabled })
                }
              />
            </div>
            <p className="mt-2 text-xs text-gray-500 pl-4">
              Note: Products listed on any invoice cannot be deleted (protected by business rules).
            </p>
          </div>

          {/* Grace Period */}
          <div className="border-t border-red-200 pt-4">
            <h3 className="text-sm font-medium text-gray-900 mb-3">Grace Period (Recommended)</h3>
            <div className="space-y-4 pl-4 border-l-2 border-red-200">
              <SyncToggle
                label="Enable grace period before deletion"
                description="Delay cascade deletes to allow time for cancellation if needed"
                enabled={formData.cascadeDeleteGracePeriodEnabled || false}
                onChange={(enabled) =>
                  setFormData({ ...formData, cascadeDeleteGracePeriodEnabled: enabled })
                }
              />
              {formData.cascadeDeleteGracePeriodEnabled && (
                <div className="pl-8">
                  <label
                    htmlFor="gracePeriodHours"
                    className="block text-sm font-medium text-gray-700"
                  >
                    Grace period duration
                  </label>
                  <select
                    id="gracePeriodHours"
                    value={formData.cascadeDeleteGracePeriodHours || 24}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        cascadeDeleteGracePeriodHours: parseInt(e.target.value),
                      })
                    }
                    className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                  >
                    <option value={1}>1 hour</option>
                    <option value={2}>2 hours</option>
                    <option value={4}>4 hours</option>
                    <option value={8}>8 hours</option>
                    <option value={12}>12 hours</option>
                    <option value={24}>24 hours (Recommended)</option>
                    <option value={48}>48 hours</option>
                    <option value={72}>72 hours</option>
                  </select>
                  <p className="mt-2 text-sm text-gray-500">
                    Pending deletes can be cancelled during this window via the API
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Notifications */}
          <div className="border-t border-red-200 pt-4">
            <h3 className="text-sm font-medium text-gray-900 mb-3">Delete Notifications</h3>
            <div className="pl-4 border-l-2 border-red-200">
              <SyncToggle
                label="Send email notification on cascade delete"
                description="Receive an email notification whenever a cascade delete is executed"
                enabled={formData.notifyOnCascadeDelete || false}
                onChange={(enabled) =>
                  setFormData({ ...formData, notifyOnCascadeDelete: enabled })
                }
              />
            </div>
          </div>
        </div>
      </div>

      {/* Info Box */}
      <div className="rounded-lg border border-blue-200 bg-blue-50 p-4">
        <div className="flex items-start">
          <Info className="h-5 w-5 text-blue-400" />
          <div className="ml-3">
            <h3 className="text-sm font-medium text-blue-800">
              Important Notes
            </h3>
            <div className="mt-2 text-sm text-blue-700">
              <ul className="list-disc list-inside space-y-1">
                <li>Changes take effect immediately upon saving</li>
                <li>Very frequent syncs may hit API rate limits</li>
                <li>Manual sync is always available regardless of settings</li>
                <li>
                  Historical data is not automatically synced - trigger a manual
                  sync for existing data
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Uninstall Plugin - Danger Zone */}
      <div className="rounded-lg border-2 border-red-300 bg-red-50 p-6 shadow-sm">
        <div className="flex items-center space-x-2 mb-4">
          <Trash2 className="h-5 w-5 text-red-600" />
          <h2 className="text-lg font-medium text-red-900">
            Uninstall Plugin
          </h2>
          <span className="inline-flex items-center rounded-full bg-red-100 px-2.5 py-0.5 text-xs font-medium text-red-800">
            Danger Zone
          </span>
        </div>

        {/* Warning Box */}
        <div className="rounded-md border border-red-200 bg-red-100 p-4 mb-6">
          <div className="flex">
            <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0" />
            <div className="ml-3">
              <p className="text-sm text-red-800">
                <strong>Warning:</strong> This will permanently disconnect your HighLevel and Xero
                integrations and archive all sync data. This action cannot be undone.
              </p>
              <ul className="mt-2 text-sm text-red-700 list-disc list-inside space-y-1">
                <li>Both HighLevel and Xero OAuth connections will be disconnected</li>
                <li>All sync settings will be disabled</li>
                <li>All pending sync operations will be cancelled</li>
                <li>All data will be archived for 30 days, then permanently deleted</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Confirmation Input */}
        <div className="space-y-4">
          <div>
            <label
              htmlFor="uninstallConfirm"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              To confirm, type <span className="font-bold text-red-700">UNINSTALL</span> below:
            </label>
            <input
              id="uninstallConfirm"
              type="text"
              value={uninstallConfirmText}
              onChange={(e) => setUninstallConfirmText(e.target.value)}
              placeholder="Type UNINSTALL to confirm"
              disabled={isUninstalling}
              className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-red-500 focus:outline-none focus:ring-1 focus:ring-red-500 disabled:opacity-50"
            />
          </div>

          <button
            onClick={() => uninstall(uninstallConfirmText)}
            disabled={uninstallConfirmText !== 'UNINSTALL' || isUninstalling}
            className={cn(
              'inline-flex items-center space-x-2 rounded-md px-4 py-2 text-sm font-medium transition-colors',
              'bg-red-600 text-white hover:bg-red-700',
              'disabled:opacity-50 disabled:cursor-not-allowed'
            )}
          >
            <Trash2 className="h-4 w-4" />
            <span>{isUninstalling ? 'Uninstalling...' : 'Uninstall Plugin'}</span>
          </button>
        </div>
      </div>

      {/* Version Footer */}
      <div className="text-center text-xs text-gray-400 pt-4">
        Version {__APP_VERSION__}
      </div>
    </div>
  );
}
